import { useEffect, useMemo } from 'react';

import { pluralize } from 'lib/helpers/utility';
import { useGetWizardDelegatedTasks } from 'lib/hooks/queries/wizard/useGetWizardDelegatedTasks';

import Button from 'components/shared/Button/Button';
import Confirm from 'components/shared/Modal/Confirm/Confirm';

import TaskDelegationModal from '../DelegateTasks/components/TaskDelegationModal';
import InnacurateData from '../InaccurateData/InaccurateData';

import DelegateTable from './DelegateTable/DelegateTable';
import { useDelegationActions } from './hooks/useDelegationActions';
import { useDelegationState } from './hooks/useDelegationState';

const DelegationTasks = ({
  isAdmin,
  setHeaderButtonProps,
  onNext,
  addFooterActions,
  mutateOrchestratorData,
  wizardState,
  orchestratorData,
  orchestratorLoading
}) => {
  const state = useDelegationState(wizardState);

  const { data } = useGetWizardDelegatedTasks({
    params: {
      wizard_instance_id: orchestratorData.instanceId,
      get_all_delegations: true,
      practiceId: orchestratorData.practiceId,
      page: state.page,
      limit: state.limit,
      filters: state.filters
    },
    options: { enabled: Boolean(orchestratorData.instanceId), refetchOnMount: true }
  });

  const delegations = data?.data?.delegations;
  const totalItems = data?.data?.pagination?.total_steps || 0;

  const actions = useDelegationActions({
    orchestratorData,
    steps: state.steps,
    checkedSteps: state.checkedSteps,
    checkedSubsteps: state.checkedSubsteps,
    selectedStepIds: state.selectedStepIds,
    selectedSubstepIds: state.selectedSubstepIds,
    setSelectedStepIds: state.setSelectedStepIds,
    setSelectedSubstepIds: state.setSelectedSubstepIds,
    setIsDelegateModalOpen: state.setIsDelegateModalOpen,
    setIsRemoveConfirmOpen: state.setIsRemoveConfirmOpen,
    setIsRowClick: state.setIsRowClick,
    setRowAssignments: state.setRowAssignments,
    clearAllSelections: state.clearAllSelections,
    delegations
  });

  const anyDelegated = useMemo(() => {
    if (!delegations) return false;
    return (
      delegations.steps?.some((s) => s.assignments.length !== 0) ||
      delegations.substeps?.some((s) => s.assignments.length !== 0)
    );
  }, [delegations]);

  useEffect(() => {
    setHeaderButtonProps(null);
    addFooterActions([
      {
        text: 'Continue',
        onClick: !anyDelegated ? onNext : () => mutateOrchestratorData(),
        isDisabled: orchestratorLoading
      }
    ]);
  }, [anyDelegated, orchestratorLoading]);

  return (
    <>
      <div className="flex h-full w-full justify-center">
        <div className="flex h-full w-full flex-col text-left">
          {!isAdmin && (
            <p className="mb-2 text-sm text-neutral-700">
              If you are enrolled in our Virtual Office Manager and/or MedBill Insurance Billing
              Services, you can also assign specific tasks to your augmented staff, giving you even
              more support to expedite setup and administration.
            </p>
          )}
          {!isAdmin && (
            <InnacurateData
              title="Want to add these services?"
              description=""
              buttonText="Request a consultation here"
            />
          )}
          <div className="min-h-0 flex-1 pb-4">
            <DelegateTable
              steps={state.steps}
              setSteps={state.setSteps}
              delegationSteps={delegations ?? { steps: [], substeps: [] }}
              onDelegateRow={actions.handleDelegateRow}
              page={state.page}
              setPage={state.setPage}
              limit={state.limit}
              setLimit={state.setLimit}
              totalItems={totalItems}
              filters={state.filters}
              setFilters={state.setFilters}
              hideActionsBar={state.isRemoveConfirmOpen || state.isDelegateModalOpen}
              actions={() => (
                <>
                  <Button
                    text="Remove delegation"
                    size="xs"
                    color="white"
                    style="outlined"
                    onClick={actions.handleOpenRemoveConfirm}
                    className="border-neutral-400 bg-transparent font-500 text-white"
                  />
                  <Button text="Delegate" size="xs" onClick={actions.handleOpenDelegateModal} />
                </>
              )}
            />
          </div>
        </div>
      </div>
      <TaskDelegationModal
        isOpen={state.isDelegateModalOpen}
        handleClose={actions.handleCloseDelegateModal}
        instanceId={orchestratorData.instanceId}
        step_ids={state.selectedStepIds}
        substep_ids={state.selectedSubstepIds}
        assignments={state.rowAssignments}
        isRowClick={state.isRowClick}
        onDelegateSuccess={actions.handleDelegateSuccess}
      />
      <Confirm
        icon="info-filled"
        iconSize={30}
        isOpen={state.isRemoveConfirmOpen}
        handleClose={() => state.setIsRemoveConfirmOpen(false)}
        variant="primary"
        title="Remove delegation?"
        message="These steps will no longer be assigned to selected staff members. You can reassign them at any time."
        footerActions={{
          primary: {
            text: `Remove ${pluralize('delegation', state?.selectedItemsCount || 0)}`,
            onClick: actions.handleRemoveDelegation,
            color: 'danger',
            isLoading: actions.isRemoveLoading
          }
        }}
      />
    </>
  );
};

export default DelegationTasks;
